'use client';

import { EditItemQuantityButton } from '@/components/commerce/cart/editItemQuantityButton';
import { useCart } from '@/contexts/CartContext';
import { usePricing } from '@/contexts/PricingContext';

import { DEFAULT_OPTION } from '@/lib/constants/content';
import { createUrl } from '@/lib/utils';
import Link from 'next/link';
import LineItemThumbnail from './LineItemThumbnail';
import { formatPrice } from '@/utils/utilities.mjs';

const CartContent = ({ cart, variantLabels }) => {
  const pricing = usePricing();
  const { clearCart, isClearingCart, clearCartError } = useCart();

  return (
    <div className="flex flex-col gap-6 w-full pb-6 lg:mr-14 lg:max-w-[610px] lg:pb-0">
      <div>
        <h1 className="font-worksans text-primary pb-1 text-2xl font-semibold">Review Your Cart</h1>
        <div className="font-worksans text-primary">
          Free shipping for US orders
        </div>
      </div>
      <ul className='flex flex-col gap-2 md:gap-4'>
      {(cart?.lines || []).map((item, i) => {
        const merchandiseUrl = getMerchandiseUrl(item);
        const prices = pricing.cart.line(item);
        const promoCopy = prices.hasPriceDifference
          ? pricing.promotionalCopy(item.merchandise, 'normal')
          : null;

        return (
          <li
            key={item.id || item.merchandise?.id}
            data-cy="cart-item"
            data-handle={item.merchandise?.product?.handle}
            // Raw Shopify option values. The visible label can be overridden
            // per value from Contentstack, see getLineVariantLabel below.
            data-variant={(item.merchandise?.selectedOptions ?? []).map((o) => o.value).join('|')}
            className="flex w-full gap-4 p-4 border-2 border-primary bg-white"
          >
            {/* Thumbnail is its own column, as production has it. Keeping it
                inside the title block instead pushed the quantity control below
                the image, which both indented it wrong and made the card taller. */}
            <div className="flex items-center">
              <Link href={merchandiseUrl} prefetch={false}>
                <div className="relative w-14 h-14 md:w-24 md:h-24 shrink-0">
                  <LineItemThumbnail item={item} />
                </div>
              </Link>
            </div>

            <div className="flex flex-col w-full gap-4">
            <div className="flex flex-row justify-between grow">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col text-left">
                  <Link href={merchandiseUrl} prefetch={false} data-cy="cart-item-link" className="underline">
                    <div className="font-worksans text-sm md:text-base font-semibold text-primary">
                      {item.merchandise?.product?.title}
                    </div>
                  </Link>
                  <div className="font-worksans text-xs md:text-sm font-normal text-primary">
                    {getLineVariantLabel(item, variantLabels)}
                  </div>
                </div>

                <div className="flex text-right justify-start">
                  <div className="flex items-start">
                    <div className="flex items-center px-2 py-1 gap-1 border border-sand-light">
                      <EditItemQuantityButton
                        item={item}
                        type="minus"
                      />

                      <span className="font-worksans text-sm md:text-base px-1 font-semibold flex w-4 items-center justify-center cursor-default text-primary">
                        <span className="sr-only">Quantity: </span>{item.quantity}
                      </span>

                      <EditItemQuantityButton
                        item={item}
                        type="plus"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-end justify-between text-right flex-col gap-2">
                <div className="text-right normal-case text-plus-medium-gray">
                  <div className="font-worksans text-sm md:text-base font-semibold text-primary">
                    {formatPrice(prices.sale)}
                  </div>

                  {prices.hasPriceDifference && (
                    <div className="flex justify-end gap-2">
                      <span className="font-worksans text-xs md:text-sm line-through text-primary">
                        {formatPrice(prices.full)}
                      </span>
                    </div>
                  )}
                </div>
                {promoCopy && (
                  <div className="flex flex-col">
                    <span className="font-worksans font-normal text-xs md:text-sm text-primary">
                      {promoCopy}
                    </span>
                  </div>
                )}
              </div>
            </div>
            </div>
          </li>
        );
      })}
      </ul>
      <div className="flex flex-col gap-2 border-t-2 pt-4 px-4 pb-10 md:pb-0 border-primary">
        <div className="flex gap-4 justify-between">
          <button
            type="button"
            onClick={() => clearCart()}
            disabled={isClearingCart}
            className="font-worksans shrink-0 underline self-start text-base lg:text-sm font-light text-left text-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isClearingCart ? 'Clearing…' : 'Clear Cart'}
          </button>
          <div className="font-worksans font-semibold text-base text-right text-primary">
            Subtotal ({cart?.totalQuantity}{' '}
            {cart?.totalQuantity === 1 ? 'item' : 'items'}): {formatPrice(pricing.cart.subtotal(cart))}
          </div>
        </div>
        {clearCartError && (
          <span className="font-worksans text-xs text-dark-red">{clearCartError}</span>
        )}
      </div>
    </div>
  );
};

/**
 * BL-757: the variant subtitle for a cart line, with any CMS custom variant
 * label swapped in for the Shopify option value it overrides.
 *
 * Shopify's `merchandise.title` is the concatenation of every option value
 * ("Twin XL / Standard Luxe Breathable Layers"), so overriding one segment
 * means rebuilding the string from `selectedOptions`. That rebuild only happens
 * when there is actually something to replace — Shopify's own title is the
 * safer default, and reconstructing it unconditionally would risk reordering
 * segments or dropping ones we did not anticipate.
 */
function getLineVariantLabel(item, variantLabels) {
  const shopifyTitle = item.merchandise?.title;
  const options = item.merchandise?.selectedOptions;
  const labels = variantLabels?.[item.merchandise?.product?.id];

  if (!labels || !options?.length) return shopifyTitle;

  let replaced = false;
  const segments = options
    .filter(({ value }) => value !== DEFAULT_OPTION)
    .map(({ value }) => {
      // Keys are normalised in `fetchVariantLabels` the same way
      // `optionValuesMatch` compares on the PDP, so look them up that way.
      const custom = labels[(value ?? '').trim().toLowerCase()];
      if (!custom) return value;
      replaced = true;
      return custom;
    });

  return replaced ? segments.join(' / ') : shopifyTitle;
}

/**
 * Takes in a line item and returns the URL for the merchandise, based on
 * whether the item is a bundle or not.
 */
function getMerchandiseUrl(item) {
  // Find the bundle handle, if any.
  const bundleHandle = item.attributes?.find((attribute) =>
    attribute.key.includes('_bundleHandle'),
  )?.value;
  // Create search params from the selected options.
  const merchandiseSearchParams = {};
  item.merchandise.selectedOptions.forEach(({ name, value }) => {
    if (value !== DEFAULT_OPTION) {
      merchandiseSearchParams[name.toLowerCase()] = value;
    }
  });

  if (bundleHandle) return bundleHandle;

  // Prefer the CMS URL stored at add-time, Shopify handle may differ from the site slug.
  const productUrl = item.attributes?.find((a) => a.key === '_productUrl')?.value
    ?? `/products/${item.merchandise.product.handle}`;

  return createUrl(productUrl, new URLSearchParams(merchandiseSearchParams));
}

export default CartContent;
